import {useMemo} from 'react';
import {useSettings} from '@components/_context';
import {Link, LINKS, MenuEntry} from './Links';
import {useApp} from '@app';
import {SITE} from '@domain/model/Site/Site';
import {useLookIn} from '@modules/look-in/ui/hooks/useLookIn';
import {ConfigurationApplication} from '@application/Config';
import {TradeName} from '@domain/model/Config/Configuration';

const LOOKIERO_SITES_WITHOUT_BLOG: string[] = [SITE.SE, SITE.CH];
const OUTFITTERY_SITES_WITH_BLOG: string[] = [SITE.DE, SITE.FR, SITE.AT, SITE.CH];
const OUTFITTERY_SITES_WITHOUT_GIFT_CARDS: string[] = [SITE.ES];

export const useMenuLinks = (): {isLoading: boolean; links: Link[]} => {
  const {authorization} = useApp();
  const {site = '', loading: settingsLoading} = useSettings();
  const {enabled: isLookInEnabled, loading} = useLookIn();
  const {tradename} = ConfigurationApplication.retrieve();
  const isLookiero = tradename === TradeName.LOOKIERO;
  const isOutfittery = tradename === TradeName.OUTFITTERY;

  const isLoggedIn = Boolean(authorization);
  const isLoading = !site || settingsLoading || (isLoggedIn && loading);
  const isBlogAvailable =
    site &&
    ((isLookiero && !LOOKIERO_SITES_WITHOUT_BLOG.includes(site)) ||
      (isOutfittery && OUTFITTERY_SITES_WITH_BLOG.includes(site)));
  const isShopAvailable = isLoggedIn && isLookInEnabled;
  const isGiftCardAvailable = isLookiero || (site && !OUTFITTERY_SITES_WITHOUT_GIFT_CARDS.includes(site));

  return useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        links: [],
      };
    }

    const links = [
      ...(isShopAvailable ? [LINKS[MenuEntry.LOOK_IN]] : []),
      ...(isLoggedIn ? [LINKS[MenuEntry.STYLE], LINKS[MenuEntry.ACCOUNT]] : []),
      ...(isBlogAvailable ? [LINKS[MenuEntry.BLOG]] : []),
      ...(isGiftCardAvailable ? [LINKS[MenuEntry.GIFTCARD]] : []),
      LINKS[MenuEntry.HELP],
      LINKS[MenuEntry.TERMS],
    ];

    return {
      isLoading: false,
      links: links,
    };
  }, [isLoading, isShopAvailable, isLoggedIn, isBlogAvailable]);
};
